import React, { useRef, useState, useEffect, useCallback } from 'react'
import { ChevronDown, Mail, ExternalLink, FileText, Zap, Brain, Code, Sparkles, ArrowRight, Menu, X, Star, BarChart3, TrendingUp, DollarSign, CheckCircle, Shield, MapPin, Flower2 } from 'lucide-react'
import ProductsSection from './ProductsSection'
import DotGrid from './DotGrid'

const ForGenAI = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrollY, setScrollY] = useState(0)
  const mouseRef = useRef({ x: 0, y: 0 })
  
  // Controls state
  const [showControls, setShowControls] = useState(false)
  const [userName, setUserName] = useState('')
  const [nameSubmitted, setNameSubmitted] = useState(false)
  const nameDotsRef = useRef([])
  const nameAnimationRef = useRef(null)
  
  // DotGrid control state
  const [dotGridSettings, setDotGridSettings] = useState({
    baseColor: '#5227FF',
    activeColor: '#FF0000',
    proximity: 120,
    shockStrength: 5,
    autonomousSpeed: 0.5,
    effectMode: 'normal' // normal, rainbow, pulse, wave
  })


useEffect(() => {
  let timeoutId = null

  const handleScroll = () => {
    if (timeoutId) clearTimeout(timeoutId)

    timeoutId = setTimeout(() => {
      setScrollY(window.scrollY)
    }, 5000000) // Adjust debounce delay (ms) as needed
  }

  window.addEventListener('scroll', handleScroll)
  return () => {
    clearTimeout(timeoutId)
    window.removeEventListener('scroll', handleScroll)
  }
}, [])


useEffect(() => {
  const handleMouseMove = (e) => {
    mouseRef.current = { x: e.clientX, y: e.clientY };
  };
  window.addEventListener('mousemove', handleMouseMove);
  return () => window.removeEventListener('mousemove', handleMouseMove);
}, []);

  // Name feature handler
  const handleNameSubmit = useCallback((e) => {
    e.preventDefault()
    if (!userName.trim()) return
    
    // Find the DotGrid component and trigger name processing
    setNameSubmitted(true)
    setShowControls(false)
    
    // Show success message
    setTimeout(() => {
      // You can add additional feedback here if needed
    }, 1000)
  }, [userName])

  const handleUserNameChange = useCallback((e) => {
    setUserName(e.target.value)
  }, [])

  // Control handlers
  const handleColorChange = (colorType, color) => {
    setDotGridSettings(prev => ({
      ...prev,
      [colorType]: color
    }))
  }

  const handleEffectChange = (effect) => {
    setDotGridSettings(prev => ({
      ...prev,
      effectMode: effect
    }))
  }

  const resetDotGrid = () => {
    setDotGridSettings({
      baseColor: '#5227FF',
      activeColor: '#FF0000',
      proximity: 120,
      shockStrength: 5,
      autonomousSpeed: 0.5,
      effectMode: 'normal'
    })
    setNameSubmitted(false)
    setUserName('')
    nameDotsRef.current = []
    if (nameAnimationRef.current) {
      nameAnimationRef.current.kill()
    }
  }

  const particleColors = [
    'from-red-500 to-red-700',
    'from-purple-500 to-purple-700',
    'from-blue-500 to-blue-700',
    'from-pink-500 to-pink-700',
  ]

  const FloatingGradientOrb = ({ className, style }) => (
    <div
      className={`absolute rounded-full blur-3xl opacity-20 animate-pulse ${className}`}
      style={style}
    />
  )

  const navColors = {
    services: 'from-blue-400 to-blue-600',
    products: 'from-purple-400 to-purple-600',
    research: 'from-pink-400 to-red-500',
    news: 'from-orange-400 to-red-500',
    about: 'from-yellow-400 to-yellow-600'
  }

  const Header = () => (
    <header className="fixed top-0 w-full z-50 bg-black/10 backdrop-blur-2xl border-b border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          <div className="flex items-center space-x-2">
            <div className="relative">
              <div className="absolute inset-0 rounded-full blur-md opacity-40 bg-red-500 animate-pulse" />
              <img
                src="/icon_forgen_512.png"
                alt="ForGen AI Logo"
                className="relative w-10 h-10 object-contain z-10"
              />
            </div>
            <span className="text-2xl font-bold text-white">
              ForGen AI
            </span>
          </div>

          <nav className="hidden md:flex space-x-8">
          {['Services', 'Products', 'Research', 'News', 'About']
            .filter(item => item !== 'Transportation') // explicitly exclude
            .map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="text-white/80 hover:text-white transition-colors duration-300 relative group"
              >
                {item}
                <span className={`absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r ${
                  navColors[item.toLowerCase()]
                } transition-all duration-300 group-hover:w-full`}></span>
              </a>
          ))}
          <a
            href="/expert-in-the-loop"
            className="text-white/80 hover:text-white transition-colors duration-300 relative group"
          >
            Expert-in-the-Loop™
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-red-500 to-pink-500 transition-all duration-300 group-hover:w-full"></span>
          </a>
          <a
            href="https://flora.forgen.ai"
            className="text-white/80 hover:text-white transition-colors duration-300 relative group"
          >
            Flora
            <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-emerald-400 to-teal-500 transition-all duration-300 group-hover:w-full"></span>
          </a>
          {/* Registry navigation hidden */}
        </nav>

          <button
            className="md:hidden text-white p-2"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>
    </header>
  )

  const HeroSection = () => (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">

        {/* Background gradient layer */}
        <div className="absolute inset-0 z-[-1] bg-gradient-to-br from-black via-gray-900 to-red-900 opacity-90" />

        {/* DotGrid on top of background */}
        <div className="absolute inset-0 z-0 pointer-events-none">
        <DotGrid
          dotSize={10}
          gap={15}
          baseColor={dotGridSettings.baseColor}
          activeColor={dotGridSettings.activeColor}
          proximity={dotGridSettings.proximity}
          shockRadius={250}
          shockStrength={dotGridSettings.shockStrength}
          resistance={750}
          returnDuration={1.5}
          autonomousSpeed={dotGridSettings.autonomousSpeed}
          effectMode={dotGridSettings.effectMode}
          enableNameFeature={true}
          userName={userName}
          nameSubmitted={nameSubmitted}
          nameDotsRef={nameDotsRef}
          nameAnimationRef={nameAnimationRef}
          className="absolute inset-0 z-0"
        />
      </div>
      {/* Animated Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-red-900 opacity-[0.2]">
        <FloatingGradientOrb
          className="w-96 h-96 bg-gradient-to-r from-blue-500 to-purple-600 top-20 -left-20"
          style={{
            transform: `translate(${mouseRef.current.x * 0.02}px, ${mouseRef.current.y * 0.02}px)`,
          }}
        />
        <FloatingGradientOrb
          className="w-80 h-80 bg-gradient-to-r from-red-600 to-pink-500 bottom-20 -right-20"
          style={{
            transform: `translate(${mouseRef.current.x * -0.015}px, ${mouseRef.current.y * -0.015}px)`,
          }}
        />

      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 leading-snug sm:leading-tight">
            <span className="bg-gradient-to-r from-white via-red-200 to-pink-200 bg-clip-text text-transparent">
              The Future of
            </span>
            <br />
            <span className="bg-gradient-to-r from-red-400 via-pink-400 to-red-400 bg-clip-text text-transparent animate-pulse">
              Agentic AI
            </span>
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-4xl mx-auto leading-relaxed">
            We build bottom-up, domain-specific AI systems designed not to replace experts — but to empower them.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
          <button
            onClick={() => {
              const el = document.getElementById('products')
              if (el) el.scrollIntoView({ behavior: 'smooth' })
            }}
            className="group relative px-8 py-4 bg-gradient-to-r from-purple-900 to-red-700 hover:from-blue-600 hover:to-black rounded-full text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25">
            <span className="relative z-10 flex items-center">
              Explore Our Work
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-red-700 to-red-800 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
          </button>

          <button
            onClick={() => {
              const el = document.getElementById('about')
              if (el) el.scrollIntoView({ behavior: 'smooth' })
            }}className="px-8 py-4 border-2 border-white/30 rounded-full text-white font-semibold hover:bg-white/10 transition-all duration-300 hover:scale-105">
            Contact Us
          </button>
        </div>

        <div className="animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/60 mx-auto" />
        </div>
      </div>
    </section>
  )

  const ServicesSection = () => (
    <section id="services" className="relative py-24 bg-gradient-to-b from-gray-900 to-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-4">
            Our <span className={`bg-gradient-to-r ${navColors?.services} bg-clip-text text-transparent`}>Services</span>
          </h2>
          <p className="text-white/60 text-lg">
            Contact us at <a href="mailto:info@forgen.ai" className="text-red-400 hover:text-red-500 transition-colors">info@forgen.ai</a> to inquire
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: <Brain className="w-8 h-8" />,
              title: "AI & ML Consulting",
              description: "Expert advisory services in generative AI, machine learning strategies, and technology selection tailored to your business needs."
            },
            {
              icon: <Zap className="w-8 h-8" />,
              title: "RAG Frameworks & Integrations",
              description: "Implementation and customization of Retrieval-Augmented Generation (RAG) frameworks to enhance your AI systems with dynamic, external knowledge bases."
            },
            {
              icon: <Code className="w-8 h-8" />,
              title: "Custom, Domain-Specific User Applications",
              description: "Design and develop software applications with powerful document indexing, searching, querying, and generating capabilities as well as intuitive user interfaces."
            },
            {
              icon: <Sparkles className="w-8 h-8" />,
              title: "Machine Learning Training",
              description: "End-to-end machine learning model training services, including Parameter-Efficient Fine-Tuning (PEFT) for high-performance, customized AI models."
            },
            {
              icon: <Star className="w-8 h-8" />,
              title: "Research & Innovation Hire",
              description: "Collaborate with leading experts to conduct cutting-edge research in generative AI and machine learning, driving innovation within your organization."
            },
            {
              icon: <Brain className="w-8 h-8" />,
              title: "Custom AI Solutions",
              description: "Fully tailored AI systems designed to meet unique business objectives, ranging from automation tools to complex generative AI platforms."
            }
          ].map((service, index) => (
            <div
              key={index}
              className="group relative p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-red-500/50 transition-all duration-500 hover:scale-105 hover:shadow-2xl hover:shadow-red-500/20"
            >
              <div className="text-red-400 mb-4 group-hover:text-red-500 transition-colors duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-red-200 transition-colors">
                {service.title}
              </h3>
              <p className="text-white/70 leading-relaxed">
                {service.description}
              </p>
              <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 to-pink-600/0 group-hover:from-red-600/5 group-hover:to-pink-600/5 rounded-2xl transition-all duration-500"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )

  const MissionSection = () => (
    <section id="mission" className="relative py-24 bg-gradient-to-br from-red-900 via-red-900 to-black overflow-hidden">
        <div className="absolute inset-0 z-0">
    {/* Optional background image */}
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: "url('/imgs/red_sun.png')" }}
    ></div>

    {/* Dark glow overlay */}
    <div className="absolute inset-0 bg-black opacity-60 backdrop-blur-sm"></div>
  </div>

  {/* Foreground content */}
  <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
    <h2 className="text-4xl font-bold mb-6">
      <span className="bg-gradient-to-r from-red-500 to-purple-600 bg-clip-text text-transparent">
        Our Mission
      </span>
    </h2>

        <div className="space-y-8 text-lg md:text-xl text-white/90 leading-relaxed">
          <p className="bg-gradient-to-r from-white/90 to-white/70 bg-clip-text text-transparent">
            We didn't start with the idea that AI should take over the world — we started with how can AI help us be more proficient at our profession.
            As engineers, professors, authors, lawyers, and domain professionals, we asked a simple question: <em className="text-red-300">How can this tech help us do our jobs better?</em>
          </p>
          <p className="bg-gradient-to-r from-white/90 to-white/70 bg-clip-text text-transparent">
            From that starting point, we've built bottom-up, domain-specific AI systems designed not to replace experts — but to empower them.
            Our mission is to make AI useful where it matters most: in the details, decisions, and workflows that define real-world expertise.
          </p>
        </div>
      </div>
    </section>
  )

  const MetricsSection = () => {
    const [counters, setCounters] = useState({
      linesOfCode: 0,
      timeSaved: 0,
      valueSaved: 0,
      projectsCompleted: 0,
      dimensionsStored: 0,
      objectCount: 0
    })

    useEffect(() => {
      const duration = 3000 // 3 seconds
      const steps = 60
      const interval = duration / steps

      const targets = {
        linesOfCode: 410564,
        timeSaved: 2973,
        valueSaved: 460000,
        projectsCompleted: 141,
        dimensionsStored: 761728512,
        objectCount: 497263
      }

      let currentStep = 0
      const timer = setInterval(() => {
        currentStep++
        const progress = currentStep / steps

        setCounters({
          linesOfCode: Math.floor(targets.linesOfCode * progress),
          timeSaved: Math.floor(targets.timeSaved * progress),
          valueSaved: Math.floor(targets.valueSaved * progress),
          projectsCompleted: Math.floor(targets.projectsCompleted * progress),
          dimensionsStored: Math.floor(targets.dimensionsStored * progress),
          objectCount: Math.floor(targets.objectCount * progress)
        })

        if (currentStep >= steps) {
          clearInterval(timer)
          setCounters(targets)
        }
      }, interval)

      return () => clearInterval(timer)
    }, [])

    return (
      <section id="metrics" className="relative py-24 bg-gradient-to-br from-blue-900 via-indigo-900 to-black overflow-hidden">
        {/* Background security pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-8 left-12 w-20 h-20 border-2 border-blue-300 rounded-full animate-pulse"></div>
          <div className="absolute top-16 right-16 w-16 h-16 border-2 border-blue-300 rounded-lg rotate-45 animate-pulse"></div>
          <div className="absolute bottom-12 left-20 w-12 h-12 border-2 border-blue-300 rounded-full animate-pulse"></div>
          <div className="absolute bottom-20 right-12 w-14 h-14 border-2 border-blue-300 rounded-lg rotate-12 animate-pulse"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">
              <span className="bg-gradient-to-r from-blue-300 to-indigo-300 bg-clip-text text-transparent">
                By the Numbers
              </span>
            </h2>
            <p className="text-blue-200/80 text-lg max-w-3xl mx-auto">
              Real impact delivered through custom in-house development by our all-U.S. team
            </p>
          </div>

          {/* Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            <div className="group relative bg-gradient-to-br from-violet-800/50 to-purple-900/70 rounded-xl p-8 border border-violet-400/40 hover:border-violet-300/60 transition-all duration-500 hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <Code className="w-8 h-8 text-violet-300" />
                <div className="bg-violet-600/60 px-3 py-1 rounded-full">
                  <span className="text-violet-100 text-sm font-medium">PROPRIETARY</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-white mb-2 font-mono">
                {counters.linesOfCode.toLocaleString()}+
              </div>
              <div className="text-violet-200/90 text-sm">Lines of Python + JS Code</div>
              <div className="text-violet-300/70 text-xs mt-1">100% in-house development</div>
            </div>

            <div className="group relative bg-gradient-to-br from-green-800/40 to-emerald-900/60 rounded-xl p-8 border border-green-500/30 hover:border-green-400/50 transition-all duration-500 hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <TrendingUp className="w-8 h-8 text-green-300" />
                <div className="bg-green-700/50 px-3 py-1 rounded-full">
                  <span className="text-green-200 text-sm font-medium">RESOURCES SAVED</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-white mb-2 font-mono">
                {counters.timeSaved.toLocaleString()}+
              </div>
              <div className="text-green-200/80 text-sm">Hours of Time Saved</div>
              <div className="text-green-300/60 text-xs mt-1">
                <span className="font-bold text-sm text-green-200/80 font-mono">${counters.valueSaved.toLocaleString()}+</span> cost savings delivered
              </div>
            </div>

            <div className="group relative bg-gradient-to-br from-red-800/40 to-orange-900/60 rounded-xl p-8 border border-red-500/30 hover:border-red-400/50 transition-all duration-500 hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <BarChart3 className="w-8 h-8 text-red-300" />
                <div className="bg-red-700/50 px-3 py-1 rounded-full">
                  <span className="text-red-200 text-sm font-medium">SHIPPED</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-white mb-2 font-mono">
                {counters.projectsCompleted}+
              </div>
              <div className="text-red-200/80 text-sm">Projects Completed</div>
              <div className="text-red-300/60 text-xs mt-1">AI tools & systems</div>
            </div>

            <div className="group relative bg-gradient-to-br from-teal-800/50 to-cyan-900/70 rounded-xl p-8 border border-teal-400/40 hover:border-teal-300/60 transition-all duration-500 hover:scale-105">
              <div className="flex items-center justify-between mb-4">
                <Brain className="w-8 h-8 text-teal-300" />
                <div className="bg-teal-600/60 px-3 py-1 rounded-full">
                  <span className="text-teal-100 text-sm font-medium">AI DATA</span>
                </div>
              </div>
              <div className="text-3xl font-bold text-white mb-2 font-mono">
                {counters.dimensionsStored.toLocaleString()}
              </div>
              <div className="text-teal-200/90 text-sm">Dimensions Stored</div>
              <div className="text-teal-300/70 text-xs mt-1">
                {counters.objectCount.toLocaleString()} objects indexed
              </div>
            </div>
          </div>

          {/* Trust Banner */}
          <div className="relative bg-gradient-to-br from-blue-900/30 via-indigo-900/20 to-blue-800/30 border border-blue-400/40 rounded-xl p-8">
            {/* Header with shield */}
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <Shield className="w-10 h-10 text-blue-300 drop-shadow-lg" />
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-3 h-3 text-green-900" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-blue-200 ml-4 tracking-wide">BUILT IN AMERICA</h3>
            </div>
            
            {/* Content cards */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-800/40 to-blue-900/60 rounded-lg p-6 border border-blue-500/30">
                <div className="flex items-center mb-3">
                  <Code className="w-6 h-6 text-blue-300 mr-3" />
                  <h4 className="font-bold text-blue-200">Custom In-House Code</h4>
                </div>
                <p className="text-blue-100/80 mb-1">100% proprietary Python framework</p>
                <p className="text-blue-300/60 text-sm">Developed from scratch by our team</p>
              </div>
              
              <div className="bg-gradient-to-br from-blue-800/40 to-blue-900/60 rounded-lg p-6 border border-blue-500/30">
                <div className="flex items-center mb-3">
                  <MapPin className="w-6 h-6 text-blue-300 mr-3" />
                  <h4 className="font-bold text-blue-200">All-U.S. Team</h4>
                </div>
                <p className="text-blue-100/80 mb-1">Domestic development workforce</p>
                <p className="text-blue-300/60 text-sm">Headquartered in Metro-Detroit, Michigan</p>
              </div>
            </div>
            
            {/* Bottom security badge */}
            <div className="mt-6 flex items-center justify-center">
              <div className="bg-blue-800/50 px-4 py-2 rounded-full border border-blue-400/30">
                <span className="text-blue-200 text-sm font-medium">SECURE • TRUSTED • AMERICAN-MADE</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  }

  const ResearchSection = () => (
    <section id="research" className="relative py-24 bg-black overflow-hidden">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-4">
            Innovations & <span className={`bg-gradient-to-r ${navColors?.research} bg-clip-text text-transparent`}>Research</span>
          </h2>
        </div>

        <div className="grid gap-8">
          {[
            {
              title: "Agentic Tooling Framework",
              description: "A novel three-phased serial-processing node architecture designed to empower intelligent agents with autonomous tool generation, execution, and orchestration capabilities. The framework introduces automatic runtime tool generation, modular serialization, agent-in-agent interoperability, and secure platform-level distribution mechanisms for scalable agent ecosystems."
            },
            {
              title: "Automatic Tool Generator",
              description: "Dynamic, model-driven utility that enables AI agents to synthesize tools from prompt context, runtime objectives, or system instructions, allowing adaptive augmentation without requiring predefined capabilities. Supports zero-shot tool invocation, validation, and integration into ongoing workflows."
            },
            {
              title: "Agentic Online Tool Lifecycle Management",
              description: "Real-time infrastructure enabling agents to instantiate, update, serialize, and decommission tools on demand during task execution. Ensures continuity, auditability, and version-controlled traceability across nodes and agent states."
            },
            {
              title: "Agentic Model Context Protocol (AMCP) Cloud",
              description: "A cloud-hosted lifecycle framework leveraging AMCP principles to orchestrate agent memory, dynamic context handoff, and multi-agent coordination. Built on top of Anthropic's MCP structure, this implementation focuses on agent modularity, persistence, and contextual continuity across tools and nested agents."
            },
            {
              title: "Agent-within-an-Agent (AiA) Interoperability",
              description: "Infrastructure and protocols supporting recursive agent behavior, enabling encapsulated agents to act as tools or subroutines within parent agents. Designed to allow nested execution, delegated cognition, and scalable task decomposition."
            },
            {
              title: "Tool Distribution Platform",
              description: "A secure, decentralized platform for distributing AI tools, utilities, and modules across agent ecosystems. Features include author attribution, usage metadata, version tracking, and runtime tool validation."
            },
            {
              title: "ML Training & Transformer Technologies",
              description: "Expertise in training state-of-the-art transformer models with a special focus on Parameter-Efficient Fine-Tuning (PEFT), optimizing AI model performance and efficiency.",
              hasDownload: true
            }
          ].map((research, index) => (
            <div
              key={index}
              className="group relative p-8 rounded-2xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-gray-700/50 hover:border-red-500/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-red-500/20"
            >
              <div className="absolute top-4 right-4">
                <span className="text-sm text-red-400 italic font-medium">Patent Pending</span>
              </div>

              <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-red-200 transition-colors">
                {research.title}
              </h3>
              <p className="text-white/80 leading-relaxed mb-4">
                {research.description}
              </p>

              {research.hasDownload && (
                <a
                  href="/pdfs/LearnableGradientAccumulation-v1.pdf"
                  className="inline-flex items-center space-x-2 text-red-400 hover:text-red-500 transition-colors group/link"
                >
                  <FileText className="w-5 h-5" />
                  <span className="font-medium group-hover/link:underline">
                    Learnable Gradient Accumulation (PDF) – May 8, 2025
                  </span>
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  )

  const FloraSection = () => (
    <section id="flora" className="relative py-24 bg-gradient-to-br from-emerald-950 via-gray-900 to-black overflow-hidden">
      <FloatingGradientOrb className="w-96 h-96 bg-gradient-to-r from-emerald-500 to-teal-600 -top-24 -right-24" />
      <FloatingGradientOrb className="w-72 h-72 bg-gradient-to-r from-orange-400 to-pink-500 bottom-0 -left-20" />

      <div className="relative z-10 max-w-6xl mx-auto px-4">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1 mb-6 rounded-full border border-emerald-400/40 bg-emerald-900/30">
            <Flower2 className="w-4 h-4 text-emerald-300" />
            <span className="text-emerald-200 text-sm font-medium tracking-wide">INTRODUCING FLORA · PATENT PENDING</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Hundreds of custom models.{' '}
            <span className="bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">One forward pass.</span>
          </h2>
          <p className="text-white/80 text-lg max-w-3xl mx-auto leading-relaxed">
            Flora is our shared-pass training technique for fleets of LoRA and QLoRA adapters. When adapters share a prompt,
            the frozen base LLM runs it once, and the cached output trains every adapter in the pool.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {[
            {
              title: 'Shared Phase',
              description: 'Common template prompts go through the base model once. The cached output is reused by every adapter instead of being recomputed for each one.'
            },
            {
              title: 'Individualized Phase',
              description: 'Prompts unique to a single tenant train as usual. Only the duplicated work is removed.'
            },
            {
              title: 'Built for Multi-Tenant AI',
              description: 'Per-business chatbots, game NPCs, hospitality assistants and "me models", all trained on one frozen base LLM.'
            }
          ].map((item, index) => (
            <div
              key={index}
              className="p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-emerald-400/20 hover:border-emerald-400/50 transition-all duration-500 hover:scale-105"
            >
              <h3 className="text-xl font-semibold text-emerald-200 mb-3">{item.title}</h3>
              <p className="text-white/70 leading-relaxed">{item.description}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <a
            href="https://flora.forgen.ai"
            className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 rounded-full text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-emerald-500/25"
          >
            Explore Flora
            <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  )

  const NewsSection = () => (
    <section id="news" className="relative py-24 bg-gradient-to-br from-gray-900 to-black overflow-hidden">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-white mb-4">
            ForGen AI in the <span className={`bg-gradient-to-r ${navColors?.news} bg-clip-text text-transparent`}>News</span>
          </h2>
        </div>

        <div className="space-y-8">
          {[
            {
              title: "ForGen AI Launches Agentic AI Research Assistant Internship for Summer 2025",
              excerpt: "ForGen AI has officially kicked off its inaugural Agentic AI Research Assistant Internship for Summer 2025. This program is designed to explore the frontiers of autonomous systems...",
              date: "June 17, 2025",
              isInternal: true
            },
            {
              title: "No, Anthropic RAG Was Never Dying — Here's Why",
              excerpt: "Amid speculation about the decline of RAG techniques, ForGen AI unpacks why Retrieval-Augmented Generation (RAG) is not only alive but foundational to the next generation...",
              date: "June 14, 2025",
              url: "https://blog.forgen.ai/no-anthropic-rag-was-never-dying-heres-why-8c7498b67725"
            },
            {
              title: "From MapReduce to MapReduceProduce: Enabling Automated Orchestration of Domain-Specific AI Toolchains",
              excerpt: "In the world of large-scale data processing, few paradigms have been as transformative as MapReduce. But as we enter the era of intelligent systems...",
              date: "May 29, 2025",
              url: "https://blog.forgen.ai/from-mapreduce-to-map-reduce-produce-a-new-paradigm-for-agentic-ai-668375de2329"
            },
            {
              title: "Build Your Own Patent Agents: How AI-Patents.com Lets You Customize Continuous IP Discovery",
              excerpt: "AI-Patents.com doesn't just provide a static tool. It gives you a customizable agentic framework, so you can tailor how your innovation is monitored...",
              date: "May 29, 2025",
              url: "https://blog.forgen.ai/build-your-own-patent-agents-how-ai-patents-com-lets-you-customize-continuous-ip-discovery-188af19ff299"
            }
          ].map((article, index) => (
            <article
              key={index}
              className="group relative p-8 rounded-2xl bg-gradient-to-br from-white/5 to-white/10 backdrop-blur-sm border border-white/10 hover:border-red-500/50 transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl hover:shadow-red-500/20"
            >
              <h3 className="text-2xl font-semibold text-white mb-4 group-hover:text-red-200 transition-colors">
                {article.title}
              </h3>
              <p className="text-white/80 leading-relaxed mb-4">
                {article.excerpt}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-sm text-white/60">
                  Published: {article.date}
                </span>
                {article.url && (
                  <a
                    href={article.url}
                    className="inline-flex items-center space-x-2 text-red-400 hover:text-red-500 transition-colors group/link"
                  >
                    <span className="group-hover/link:underline">Read full article</span>
                    <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  )

  const AboutSection = () => (
    <section id="about" className="relative py-24 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold text-white mb-8">
          Who <span className={`bg-gradient-to-r ${navColors?.about} bg-clip-text text-transparent`}>We Are</span>
        </h2>

        <div className="space-y-8 text-lg text-white/90 leading-relaxed max-w-4xl mx-auto">
          <p>
            ForGen AI is a team of engineers, lawyers, and business professionals focused on building truly domain-integrated, cognitive AI systems.
            We're not a big-tech derivative — we're a bottom-up, AI-first company.
          </p>
          <p>
            Our founding team consists of University of Michigan College of Engineering alumni with deep technical experience in machine learning, systems architecture, and agentic AI.
            Complementing that core is a group of seasoned legal advisors and financial consultants who understand how advanced AI systems intersect with IP law, risk governance, and operational scalability.
          </p>
          <p>
            We focus on building from the ground up — designing general techniques in Retrieval-Augmented Generation (RAG), Parameter-Efficient Fine-Tuning (PEFT), and dynamic tool orchestration,
            always with the lens of how they map onto intuitive, domain-specific human reasoning patterns.
          </p>
          <p>
            Our <strong className="text-red-300">"Map → Reduce → Produce"</strong> philosophy reflects our approach to intelligent problem-solving:
            start with comprehensive understanding, reduce to what's essential, and produce targeted, actionable solutions via agentic frameworks.
          </p>
          <p className="mb-12">
            Whether building patent agent systems, generative research assistants, or AI-native user platforms, our mission is the same:
            align the next wave of AI innovation with real-world, intuitive, and high-context cognitive workflows.
          </p>
        </div>

        <a
          href="mailto:info@forgen.ai"
          className="group mt-6 relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-red-600 to-red-600 rounded-full text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25"
        >
          <Mail className="mr-2 w-5 h-5" />
          Get in Touch
        </a>
      </div>
    </section>
  )

  const BlogSection = () => (
    <section id="blog" className="relative py-24 bg-black">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h2 className="text-5xl font-bold text-white mb-4">
          Read Our <span className="bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent">Blog</span>
        </h2>
        <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">
          Stay updated with our latest insights, research breakdowns, and engineering deep dives in generative AI.
        </p>

        <a
          href="https://blog.forgen.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="group relative inline-flex items-center px-8 py-4 bg-gradient-to-r from-red-600 to-red-600 rounded-full text-white font-semibold transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-red-500/25"
        >
          <ExternalLink className="mr-2 w-5 h-5" />
          Visit blog.forgen.ai
        </a>
      </div>
    </section>
  )

  const Footer = () => (
    <footer className="relative bg-gray-900 text-white py-12 border-t border-gray-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex flex-col md:flex-row justify-between items-center">
          <div className="flex items-center space-x-2 mb-4 md:mb-0">
          <img
            src="/icon_forgen_512.png"
            alt="ForGen AI Logo"
            className="w-10 h-10 object-contain"
          />
          <span className="text-2xl font-bold text-white">
            ForGen AI
          </span>

          </div>

          <div className="flex flex-wrap justify-center md:justify-end space-x-6 text-sm text-white/70">
            <a href="#services" className="hover:text-white transition-colors">Services</a>
            <a href="#products" className="hover:text-white transition-colors">Products</a>
            <a href="#research" className="hover:text-white transition-colors">Research</a>
            <a href="#news" className="hover:text-white transition-colors">News</a>
            <a href="#about" className="hover:text-white transition-colors">About</a>
            <a href="/expert-in-the-loop" className="hover:text-white transition-colors">Expert-in-the-Loop™</a>
            <a href="https://flora.forgen.ai" className="hover:text-white transition-colors">Flora</a>
            <a href="mailto:info@forgen.ai" className="hover:text-white transition-colors">Contact</a>
          </div>
        </div>

        <div className="mt-8 pt-8 border-t border-gray-800 text-center text-sm text-white/60">
          <p>&copy; 2024–2025 ForGen AI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <Header />
      <HeroSection />
      <ProductsSection />
      <ServicesSection />
      <MissionSection />
      <MetricsSection />
      <ResearchSection />
      <FloraSection />
      <NewsSection />
      <AboutSection />
      <BlogSection />
      <Footer />

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm md:hidden">
          <div className="flex flex-col items-center justify-center h-full space-y-8">
            {['Services', 'Products', 'Research', 'News', 'About'].map((item) => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="text-2xl text-white hover:text-red-400 transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item}
              </a>
            ))}
            <a
              href="/expert-in-the-loop"
              className="text-2xl text-white hover:text-red-400 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Expert-in-the-Loop
            </a>
            <a
              href="https://flora.forgen.ai"
              className="text-2xl text-white hover:text-red-400 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Flora
            </a>
            <a
              href="/registry"
              className="text-2xl text-white hover:text-red-400 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Registry
            </a>
            <a
              href="mailto:info@forgen.ai"
              className="text-2xl text-white hover:text-red-400 transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

export default ForGenAI